// T-281 — Archivio stampanti DEVICE-LOCAL (Capacitor Preferences, funziona su
// native e PWA). Una stampante è una periferica del dispositivo: disponibile in
// TUTTI i locali, non sincronizzata, non multi-tenant. Sostituisce i master
// `printers` tenant-scoped per la selezione in app.

import { useCallback, useEffect, useState } from 'react';
import { Preferences } from '@capacitor/preferences';
import {
  EMPTY_STORE,
  type DevicePrinter,
  type DevicePrinterStore,
} from './device-printers-core.js';

const KEY = 'trackeat.devicePrinters.v1';
export const DEVICE_PRINTERS_CHANGED = 'trackeat:devicePrinters';

const sameDevice = (a: DevicePrinter, b: DevicePrinter): boolean =>
  a.driver === b.driver && a.address === b.address;

async function readStore(): Promise<DevicePrinterStore> {
  try {
    const { value } = await Preferences.get({ key: KEY });
    if (!value) return { ...EMPTY_STORE };
    const parsed = JSON.parse(value) as Partial<DevicePrinterStore>;
    return {
      printers: Array.isArray(parsed.printers) ? parsed.printers : [],
      defaultId: parsed.defaultId ?? null,
    };
  } catch {
    return { ...EMPTY_STORE };
  }
}

async function writeStore(store: DevicePrinterStore): Promise<void> {
  await Preferences.set({ key: KEY, value: JSON.stringify(store) });
  try {
    window.dispatchEvent(new CustomEvent(DEVICE_PRINTERS_CHANGED));
  } catch {
    /* nessun window (test/SSR) */
  }
}

export async function getDevicePrinterStore(): Promise<DevicePrinterStore> {
  return readStore();
}

/** Aggiunge (o aggiorna) una stampante; diventa predefinita se non ce n'è una. */
export async function addDevicePrinter(printer: DevicePrinter, makeDefault = false): Promise<void> {
  const store = await readStore();
  const without = store.printers.filter((p) => !sameDevice(p, printer));
  const printers = [...without, printer];
  const defaultStillValid = store.defaultId != null && printers.some((p) => p.id === store.defaultId);
  const defaultId = makeDefault || !defaultStillValid ? printer.id : store.defaultId;
  await writeStore({ printers, defaultId });
}

export async function removeDevicePrinter(id: string): Promise<void> {
  const store = await readStore();
  const printers = store.printers.filter((p) => p.id !== id);
  const defaultId = store.defaultId === id
    ? (printers.find((p) => p.driver !== 'preview')?.id ?? printers[0]?.id ?? null)
    : store.defaultId;
  await writeStore({ printers, defaultId });
}

export async function setDefaultDevicePrinter(id: string): Promise<void> {
  const store = await readStore();
  if (!store.printers.some((p) => p.id === id)) return;
  await writeStore({ printers: store.printers, defaultId: id });
}

/**
 * Import-on-sight dei master legacy tenant-scoped non ancora presenti nel
 * device store (dedup per driver+address). Non distruttivo. Ritorna quanti ne
 * ha aggiunti (0 = niente da fare).
 */
export async function importLegacyPrinters(legacy: DevicePrinter[]): Promise<number> {
  if (legacy.length === 0) return 0;
  const store = await readStore();
  const toAdd = legacy.filter((leg) => !store.printers.some((p) => sameDevice(p, leg)));
  if (toAdd.length === 0) return 0;
  const printers = [...store.printers, ...toAdd];
  const defaultId = store.defaultId ?? printers.find((p) => p.driver !== 'preview')?.id ?? null;
  await writeStore({ printers, defaultId });
  return toAdd.length;
}

/** Hook reattivo: ricarica quando il device store cambia (altra pagina). */
export function useDevicePrinters(): {
  printers: DevicePrinter[];
  defaultId: string | null;
  loaded: boolean;
  reload: () => void;
} {
  const [store, setStore] = useState<DevicePrinterStore>(EMPTY_STORE);
  const [loaded, setLoaded] = useState(false);
  const reload = useCallback(() => {
    void getDevicePrinterStore().then((s) => { setStore(s); setLoaded(true); });
  }, []);
  useEffect(() => {
    reload();
    const onChange = (): void => reload();
    window.addEventListener(DEVICE_PRINTERS_CHANGED, onChange);
    return () => window.removeEventListener(DEVICE_PRINTERS_CHANGED, onChange);
  }, [reload]);
  return { printers: store.printers, defaultId: store.defaultId, loaded, reload };
}

export type { DevicePrinter, DevicePrinterStore };
